import React, { useRef } from "react"
import tw, { styled, css } from "twin.macro"
import { useEventListener } from "../../../utils/hooks/useEventListener"
import { ReactComponent as SpinnerSvg } from "../../../assets/icons/spinner.svg"

export enum InputType {
  Password = "password",
  Email = "email",
  Text = "text",
  Tel = "tel",
  Number = "number",
  Currency = "currency",
}
export interface InputProps {
  inputRef?: React.MutableRefObject<null>
  inputValue: string
  name: string
  type?: InputType
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleBlur?: () => Promise<void> | void
  handleFocus?: () => Promise<void> | void
  disabled?: boolean
  required?: boolean
  maxLength?: number
  min?: string
  autoComplete?: string
  label?: string
  description?: string
  placeholder?: string
  helpText?: string
  tabIndex?: number
  invalid?: boolean
  autoFocus?: boolean
  hasFocus?: boolean
  loading?: boolean
  readOnly?: boolean
}
interface StyledProps {
  invalid: boolean | undefined
  type?: string
}

export const Field = styled.div(() => [tw`w-full space-y-2 max-w-screen-mobile-lg min-w-card`])
export const Label = styled.label(() => [tw`text-md text-body-color font-bold`])
export const Description = styled("p")(() => [tw`text-sm text-base-4 m-0`])
export const StyledSpinner = styled(SpinnerSvg)(() => [tw`absolute right-3.5 top-3.5 animate-spin h-3.5 w-3.5`])
export const HelpText = styled("p")(({ invalid }: StyledProps) => [
  tw`text-xs text-base-3 font-bold min-h-[18px]`,
  invalid && tw`text-red`,
])

const StyledInput = styled.input(({ invalid }: StyledProps) => [
  tw`w-full p-2`,
  tw`text-base-6 bg-input caret-purple`,
  tw`(overflow-hidden whitespace-nowrap overflow-ellipsis)`,
  tw`(border border-base-3 rounded)`,
  tw`placeholder:(text-base-3)`,
  tw`focus:(outline-none border-purple)`,
  tw`disabled:(text-base-2 border-base-2 placeholder:(text-base-2))`,
  tw`read-only:(focus:border-base-3)`,
  invalid && tw`(border-red)`,
  css`
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  `,
])

export const Input = ({
  inputValue,
  name,
  type,
  handleChange,
  handleKeyDown,
  handleKeyUp,
  handleFocus,
  handleBlur,
  autoFocus,
  maxLength,
  min,
  disabled,
  required,
  autoComplete,
  tabIndex,
  label,
  description,
  placeholder,
  helpText,
  invalid,
  loading,
  readOnly,
}: InputProps) => {
  const inputRef = useRef(null)
  useEventListener("blur", () => handleBlur?.(), inputRef)

  return (
    <Field>
      <Label data-testid={`${name}-label`} htmlFor={`${name}-id`}>
        {label}
      </Label>
      {description && (
        <Description id={`${name}-description`} data-testid={`${name}-description`}>
          {description}
        </Description>
      )}
      <div tw="relative">
        <StyledInput
          ref={inputRef}
          data-testid={`${name}-input`}
          autoComplete={autoComplete ? autoComplete : "off"} // value has dashes will not work in chrome
          tabIndex={tabIndex}
          type={type}
          name={name}
          id={`${name}-id`}
          onChange={handleChange}
          autoFocus={autoFocus}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          value={inputValue}
          placeholder={placeholder}
          aria-label={label}
          aria-describedby={`${name}-description ${name}-helptext`}
          maxLength={maxLength}
          min={min}
          disabled={disabled || loading}
          required={required}
          aria-required={required}
          invalid={invalid}
          readOnly={readOnly}
          aria-readonly={readOnly}
        />
        {loading && <StyledSpinner data-testid="spinner" />}
      </div>
      <HelpText id={`${name}-helptext`} data-testid={`${name}-help-text`} invalid={invalid}>
        {helpText}
      </HelpText>
    </Field>
  )
}
